import { IApiBaseProps } from '@common/api.models';
import { createGetOptionsWithSearchFn } from '@common/selectOptions.services';
import { envConfigs } from '@config/envConfigs';
import { convertRequestToFormData } from '@file/file.services';
import { IItemResponse, IListResponse, IPagedResultsV1, textIsNullOrEmpty } from '@gcl/react-core';
import { apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { IUpdateUserProfileRoleRequest, IUserProfileRole } from '@role/userprofileRole';
import {
  IUserProfile,
  ICreateMySignatureRequest,
  ISignature,
  IUpdateMySignatureRequest,
  IUserProfileOption,
  IProjectUserProfileWithTeamOption,
  IUpdateUserProfileRequest,
} from './userProfile.models';
import { ISyncTrainingRecordsByUserRequest } from './training.models';

export const apiRoutes = {
  createMySignature: '/Signatures/MySignature',
  updateMySignature: '/Signatures/MySignature',
  getMySignature: '/Signatures/MySignature',
  getAllUserProfiles: '/UserProfiles',
  getUserProfile: '/UserProfiles',
  getUserProfileOptionsByCompany: '/UserProfiles/Options/ByCompany',
  srpOptions: '/Users/Srp/Options/Search',
  twcOptions: '/Users/Twc/Options/Search',
  twcmOptions: '/Users/Twcm/Options/Search',
  syncUserProfile: '/UserProfiles/Sync',
  getGlobalRolesByUserProfileId: `/UserProfiles/GlobalRoles/ByUserProfileId`,
  userProfilesGlobalRolesWithId: (id: number) => `/UserProfiles/GlobalRoles/${id}`,
  userProfileWithId: (id: number) => `/UserProfiles/${id}`,
  syncTrainingRecordsByUser: `/Trainings/SyncByUser`,
};

export const getMySignatureAsync = async (): Promise<IItemResponse<ISignature>> => {
  const result = await apiServerGet<IItemResponse<ISignature>>(`${envConfigs.apiBaseUrl}${apiRoutes.getMySignature}`);
  return result.data;
};

export const createMySignatureAsync = async (
  createSignatureRequest: ICreateMySignatureRequest,
): Promise<IItemResponse<ISignature>> => {
  const formData = convertRequestToFormData(createSignatureRequest);
  const result = await apiServerPost<IItemResponse<ISignature>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.createMySignature}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return result.data;
};

export const updateMySignatureAsync = async (
  updateSignatureRequest: IUpdateMySignatureRequest,
): Promise<IItemResponse<ISignature>> => {
  const formData = convertRequestToFormData(updateSignatureRequest);
  const result = await apiServerPut<IItemResponse<ISignature>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateMySignature}/${updateSignatureRequest.id}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return result.data;
};

export interface IGetUserProfileOptionsByCompanyProps extends IApiBaseProps {
  companyId: number;
}

export const getUserProfileOptionsByCompanyAsync = async ({
  ...props
}: IGetUserProfileOptionsByCompanyProps): Promise<IPagedResultsV1<IUserProfileOption>> => {
  const result = await apiServerGet<IPagedResultsV1<IUserProfileOption>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getUserProfileOptionsByCompany}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getAllUserProfileAsync = async (props: IApiBaseProps): Promise<IListResponse<IUserProfile>> => {
  const result = await apiServerGet<IListResponse<IUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getAllUserProfiles}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getUserProfileAsync = async (id: number): Promise<IItemResponse<IUserProfile>> => {
  const result = await apiServerGet<IItemResponse<IUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getUserProfile}/${id}`,
  );
  return result.data;
};

export const putUserProfileAsync = async (props: IUpdateUserProfileRequest): Promise<IItemResponse<IUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.userProfileWithId(props.id)}`,
    props,
  );
  return result.data;
};

export const getUserDisplayName = (
  displayName: string | undefined,
  firstName: string | undefined | null,
  lastName: string | undefined | null,
) => {
  if (!textIsNullOrEmpty(displayName)) return displayName;
  return `${`${firstName || ''} ${lastName || ''}`.trim()}`;
};

export interface IRemoveUserFromCompanyProps extends IApiBaseProps {
  companyId: number;
  userProfileId: number;
}

export const getSrpOptionsWithSearchAsync = createGetOptionsWithSearchFn<IProjectUserProfileWithTeamOption>(
  apiRoutes.srpOptions,
);
export const getTwcOptionsWithSearchAsync = createGetOptionsWithSearchFn<IProjectUserProfileWithTeamOption>(
  apiRoutes.twcOptions,
);
export const getTwcmOptionsWithSearchAsync = createGetOptionsWithSearchFn<IProjectUserProfileWithTeamOption>(
  apiRoutes.twcmOptions,
);

export interface ISyncUserProfileRequest {
  uid?: string;
  isExternal?: boolean;
}

export const syncUserProfileAsync = async (props: ISyncUserProfileRequest): Promise<IItemResponse<IUserProfile>> => {
  const result = await apiServerPost<IItemResponse<IUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.syncUserProfile}`,
    props,
  );
  return result.data;
};

export interface IGetGlobalRolesByUserProfileIdRequest {
  userProfileId: number;
}
export const getGlobalRolesByUserProfileId = async (
  props: IGetGlobalRolesByUserProfileIdRequest,
): Promise<IItemResponse<IUserProfileRole>> => {
  const result = await apiServerGet<IItemResponse<IUserProfileRole>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getGlobalRolesByUserProfileId}`,
    {
      params: props,
    },
  );
  return result.data;
};

export const putUserProfilesGlobalRolesAsync = async (
  props: IUpdateUserProfileRoleRequest,
): Promise<IItemResponse<IUserProfile>> => {
  const result = await apiServerPut<IItemResponse<IUserProfile>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.userProfilesGlobalRolesWithId(props.id)}`,
    props,
  );
  return result.data;
};

export const syncTrainingRecordsByUser = async (props: ISyncTrainingRecordsByUserRequest): Promise<boolean> => {
  const result = await apiServerPost<any>(`${envConfigs.apiBaseUrl}${apiRoutes.syncTrainingRecordsByUser}`, props);
  return result.status === 200;
};

export const updateUserResignedStatusAsync = async (userId: number, isResigned: boolean): Promise<boolean> => {
  const result = await apiServerPut<boolean>(`${envConfigs.apiBaseUrl}/UserProfiles/${userId}/UpdateResignedStatus`, {
    isResigned,
  });
  return result.data;
};
